import { useList, useTranslate } from "@refinedev/core";
import { useOrganization } from "hooks/useOrganization";
import { PageLayout } from "components/PageLayout";
import SearchInput from "../projects/components/SearchInput";
import { useMemo, useState } from "react";
import { useAntTheme } from "hooks/useAntTheme";
import { Flex, Row, Spin, Typography } from "antd";
import { CreatePrimaryButton } from "components/CreatePrimaryButton/";
import { useWorkspace } from "hooks/useWorkspace/";
import { CreateWorkspaceModal } from "./components/CreateWorkspaceModal";
import { WorkspaceCard } from "./components/WorkspaceCard";
import { QuotaFeatures } from "@/constants";
import { EmptyList } from "./emptyList";
import { useQuota } from "#src/hooks";
import { UserLicenseAssignmentResponse } from "types";

export const WorkspacesListPage = () => {
  const t = useTranslate();
  const { quotas, isBlocked, isLoading: isLoadingSubscriptions } = useQuota();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const { theme } = useAntTheme();
  const [willCreateWorkspace, setWillCreateWorkspace] = useState(false);

  const { organization } = useOrganization({});
  const {
    workspaces,
    isLoading: isLoadingWorkspaces,
    refetch,
  } = useWorkspace();

  const { data: organizationUsersData, isLoading: isLoadingUsers } =
    useList<UserLicenseAssignmentResponse>({
      resource: `api/organizations/${organization?.id}/subscriptions/users`,
      queryOptions: {
        enabled: !!organization?.id,
      },
      pagination: {
        mode: "off",
      },
    });

  const organizationUsers = organizationUsersData?.data ?? [];

  const isWorkspaceQuotaReached = isBlocked(QuotaFeatures.Workspace);

  const isPending =
    isLoadingWorkspaces || isLoadingSubscriptions || isLoadingUsers;

  const userLicenseFeature =
    quotas?.["Libertify.Subscription.UserLicenseAssignment"];
  const workspaceFeature = quotas?.["Libertify.Workspace"];

  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery?.trim()) {
      return workspaces;
    }
    const query = searchQuery.toLowerCase();
    return workspaces.filter((workspace) =>
      workspace.name.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: "100%" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <PageLayout
      title={t("media.workspaces.index.myWorkspaces")}
      breadcrumbLabel={t("src.App.workspaces")}
      actions={
        <Flex align="center" gap={theme.margin}>
          <Typography.Text>
            {t("media.workspaces.index.usage")}: {workspaceFeature?.usage} /{" "}
            {workspaceFeature?.quota_limit}
          </Typography.Text>
          <CreatePrimaryButton
            disabled={isWorkspaceQuotaReached}
            onClick={() => {
              setWillCreateWorkspace(true);
            }}
          >
            {t("media.workspaces.index.newWorkspace")}
          </CreatePrimaryButton>
        </Flex>
      }
    >
      <CreateWorkspaceModal
        open={willCreateWorkspace}
        onCancel={() => setWillCreateWorkspace(false)}
        onSuccess={() => setWillCreateWorkspace(false)}
        quotaLimit={userLicenseFeature?.quota_limit}
        usageCount={userLicenseFeature?.usage}
        organizationId={organization?.id}
      />
      <Flex vertical gap={theme.marginLG} style={{ width: "100%" }}>
        <Flex style={{ width: "100%" }}>
          <div style={{ width: "50%" }}>
            <SearchInput
              onSearch={(value) => setSearchQuery(value)}
              value={searchQuery ?? ""}
              style={{
                color: theme.colorTextDescription,
                height: 40,
              }}
              size="large"
              placeholder={t("pages.components.ListPanel.search", {
                item: t("media.workspaces.index.workspaces").toLowerCase(),
              })}
              prefixStyle={{
                fontSize: 20,
              }}
            />
          </div>
        </Flex>
        <Row gutter={[50, 50]}>
          {filteredWorkspaces?.map((workspace) => (
            <WorkspaceCard
              key={workspace.id}
              workspace={workspace}
              organizationUsers={organizationUsers}
              onDeleteSuccess={refetch}
            />
          ))}
          {filteredWorkspaces?.length === 0 && (
            <EmptyList>
              <CreatePrimaryButton onClick={() => setWillCreateWorkspace(true)}>
                {t("media.workspaces.index.noWorkspacesYet")}
              </CreatePrimaryButton>
            </EmptyList>
          )}
        </Row>
      </Flex>
    </PageLayout>
  );
};
